import { useState, useMemo } from "react";

import useAppDispatch from "./store/useAppDispatch";
import useAppSelector from "./store/useAppSelector";
import useEvent from "./useEvent";

import {
	selectSessionGames,
	selectSessionCurrency,
	selectSessionCurrentBonus,
	selectIsSoundOn,
	selectIsQuickBetEnabled
} from "store/slices/auth/selectors";
import { createSelectBetsOrQuickBets, selectShowMobileQuickBetslip } from "store/slices/betslip/selectors";
import { selectIsBonusBetDisabled } from "store/slices/game/selectors";
import { selectUseBonus } from "store/slices/bonus/selectors";
import { clearAllTypeBetsAction, removeBetAction } from "store/slices/betslip/actions";
import { doBetThunk, placeQuickBetThunk } from "store/slices/betslip/thunks";

import { GAME_STATUSES, GAME_ACTIVITY_STATE, GAME_TYPE } from "constants/game.constants";
import { useTranslation } from "react-i18next";
import useGlobalVariables from "hooks/useGlobalVariables";

import { buildPathToStaticFolderOfCDN, playAudio, makeCurrencyText } from "utils/common";
import { BONUS_TYPE } from "constants/bonus.constants";
import { FLY_ELEMENT_OPTIONS } from "constants/animations";
import ToastManager from "helpers/toastManager";
import { generateBetKey } from "utils/bets";
import useProcessingQuickBetsStore from "./useProcessingQuickBetsStore";
import { QUICK_BET_PROCESS_STATES } from "constants/market.constants";

const useBetHandlers = ({ market, group, eventInfo }) => {
	const { t } = useTranslation();

	const globalVariables = useGlobalVariables();

	const { isMobile } = globalVariables;

	const games = useAppSelector(selectSessionGames);
	const currentBonus = useAppSelector(selectSessionCurrentBonus);
	const isSound = useAppSelector(selectIsSoundOn);
	const currency = useAppSelector(selectSessionCurrency);
	const isQuickBetEnabled = useAppSelector(selectIsQuickBetEnabled);
	const bets = useAppSelector(createSelectBetsOrQuickBets(isQuickBetEnabled));
	const isBonusBetDisabled = useAppSelector(selectIsBonusBetDisabled);
	const useBonus = useAppSelector(selectUseBonus);
	const isQuickBetslipVisible = useAppSelector(selectShowMobileQuickBetslip);

	const dispatch = useAppDispatch();

	const gameType = eventInfo?.gameType;

	const isBettingDisabled = useMemo(() => {
		const game = games.find((g) => g.type === gameType);
		return game?.state === GAME_ACTIVITY_STATE.CLOSE_FOR_BETTING;
	}, [games, gameType]);

	const {
		processingQuickBetsStore,
		addOrUpdate: addOrUpdateInProcessingQuickBetsStore,
		remove: removeFromProcessingQuickBetsStore
	} = useProcessingQuickBetsStore();

	const isFreeAmountBonus = useBonus && currentBonus?.bonusType === BONUS_TYPE.FREEAMOUNT;

	const getMinLimit = () => currency.singleMin ?? 0;
	const getMaxLimit = () => currency.singleMax ?? Infinity;
	const getMaxPossibleWin = () => currency.singlePossibleWinMax ?? Infinity;

	/** Function to check if bet is already in betslip bets
	 * @function
	 * @param {string} id
	 * @returns {boolean}
	 * @memberOf useBetHandlers
	 */
	const isBetSelected = useEvent((id, eventInfoArg = eventInfo) => {
		return bets.some((b) => b.betId === id && b.eventId === eventInfoArg.id);
	});

	/** Function to check if bet is disabled
	 * @function
	 * @param {object} bet
	 * @returns {boolean}
	 * @memberOf useBetHandlers
	 */
	const isBetDisabled = useEvent((bet, eventInfoArg = eventInfo) => {
		if (!bet) {
			return false;
		}

		return (
			bet.disabled ||
			isBettingDisabled ||
			[GAME_STATUSES.CLOSE_FOR_BETTING, GAME_STATUSES.STARTED].includes(eventInfoArg?.status)
		);
	});

	const checkIsThereFreeAmountBonusError = (stakeAmount) => {
		const totalStack = isQuickBetEnabled
			? stakeAmount
			: bets.reduce((acc, b) => {
					const stake = Number(b.stake);
					if (Number.isNaN(stake)) {
						return acc;
					}
					return acc + stake;
				}, 0);
		if (currentBonus.amount < totalStack) {
			return true;
		}
		const difference = currentBonus.amount - totalStack;
		return difference !== 0 && difference < (currency.singleMin ?? 0);
	};

	const hasBetStakeError = (bet) => {
		const isValueInRange =
			(Number(bet.stake) < (currency.singleMin ?? 0) || Number(bet.stake) > (currency.singleMax ?? Infinity)) &&
			Number(bet.stake) !== 0;

		if (isFreeAmountBonus) {
			const retVal = checkIsThereFreeAmountBonusError(bet.stake);
			if (retVal) {
				return retVal;
			}
		}

		return isValueInRange;
	};

	const isBetExpired = (bet) => {
		return bet && bet.isExpired;
	};

	/** Function which checks if "place bet" button should be disabled
	 * @function
	 * @returns {boolean}
	 * @memberOf QuickBetslip
	 */
	const isPlaceBetDisabled = (bet) => {
		const numAmount = Number(bet.stake);
		return numAmount === 0 || Number.isNaN(numAmount) || hasBetStakeError(bet) || isBetExpired(bet);
	};

	/** Function which fires on "place bet" button click
	 * @function
	 * @memberOf QuickBetslip
	 */
	const handlePlaceQuickBet = (bet, flyControls) => {
		addOrUpdateInProcessingQuickBetsStore(bet.key, QUICK_BET_PROCESS_STATES.PENDING);

		const onSuccess = () => {
			addOrUpdateInProcessingQuickBetsStore(bet.key, QUICK_BET_PROCESS_STATES.SUCCESSFUL);

			setTimeout(() => {
				flyControls.startFlying?.({
					...FLY_ELEMENT_OPTIONS.BET_HISTORY,
					startStyles: {
						backgroundColor: "var(--green-color)"
					}
				});

				// Need for fly element delayed update
				setTimeout(() => {
					removeFromProcessingQuickBetsStore(bet.key);
				}, 10);
			}, 1500);

			if (isMobile && isSound) {
				playAudio(buildPathToStaticFolderOfCDN("media/success.mp3"));
			}
		};

		const onError = (bet) => {
			setTimeout(() => {
				addOrUpdateInProcessingQuickBetsStore(bet.key, QUICK_BET_PROCESS_STATES.REJECTED);

				setTimeout(() => {
					removeFromProcessingQuickBetsStore(bet.key);
				}, 1500);

				dispatch(removeBetAction({ isQuickBet: true, key: bet.key, removeStake: false }));
			}, 100);
		};

		if (isPlaceBetDisabled(bet)) {
			if (isFreeAmountBonus && checkIsThereFreeAmountBonusError(bet.stake)) {
				ToastManager.instance.error(t("bet.unallowedStack"));
			} else if (bet.stake < getMinLimit()) {
				ToastManager.instance.error(
					`${t("bet.minBetAmountIs")} ${makeCurrencyText(getMinLimit(), {
						currencyId: currency.currencyId ?? 0,
						decimalCount: currency.decimalCount ?? 0
					})}`
				);
			} else if (bet.stake > getMaxLimit()) {
				ToastManager.instance.error(
					`${t("bet.maxBetAmountIs")} ${makeCurrencyText(getMaxLimit(), {
						currencyId: currency.currencyId ?? 0,
						decimalCount: currency.decimalCount ?? 0
					})}`
				);
			} else {
				ToastManager.instance.error(
					`${t("bet.maxPosWinAmountIs")} ${makeCurrencyText(getMaxPossibleWin(), currency)}`
				);
			}

			onError(bet);
			return;
		}

		dispatch(
			placeQuickBetThunk({
				bet,
				onSuccess,
				onError
			})
		);
	};

	/** Function which fires on bet click
	 * @function
	 * @param {object} marketArg - market to bet
	 * @memberOf useBetHandlers
	 */
	const handleClick = useEvent((_, flyControls, marketArg, eventInfoArg = eventInfo) => {
		const isSelected = isBetSelected(marketArg.id);
		const isDisabled = isBetDisabled(marketArg);
		const oddKey = generateBetKey({ eventId: eventInfoArg.id, betId: marketArg.id, factor: marketArg.factor });

		if (isBonusBetDisabled) {
			ToastManager.instance.error(t("bonus.betUnallowed"));
			return;
		}
		if (eventInfoArg.status !== GAME_STATUSES.NEW && eventInfoArg.status !== GAME_STATUSES.PREAMBLE_STARTED) return;

		if (!isDisabled || isSelected) {
			if (isQuickBetslipVisible && isSelected) {
				dispatch(clearAllTypeBetsAction());
				return;
			}

			if (isQuickBetslipVisible && isQuickBetEnabled) {
				return;
			}

			if (isQuickBetEnabled && (isSelected || Boolean(processingQuickBetsStore[oddKey]))) {
				return;
			}

			dispatch(
				doBetThunk({
					isQuickBet: isQuickBetEnabled,
					market: { ...marketArg, period: market?.period },
					group,
					marketGroup: market?.group,
					marketPeriod: market?.period,
					eventInfo: eventInfoArg,
					stake: "",
					onSuccess: (bet) => {
						if (isQuickBetEnabled) {
							handlePlaceQuickBet(bet, flyControls);
						} else {
							// First Bet In Mobile
							if (isMobile && gameType !== GAME_TYPE.LUCKY_SIX && bets.length === 0) {
								return;
							}

							flyControls.startFlying?.({
								...(isMobile ? FLY_ELEMENT_OPTIONS.BET_SLIP_MOBILE : FLY_ELEMENT_OPTIONS.BET_SLIP_DESKTOP)
							});
						}
					},
					globalVariables
				})
			);
		}
	});

	return {
		isBetSelected,
		isBetDisabled,
		isBettingDisabled,
		isBonusBetDisabled,
		processingQuickBetsStore,
		handleClick,
		handlePlaceQuickBet
	};
};

export default useBetHandlers;
